/**
 * Station Mapper
 *
 * Maps between ATC manifest types (@/types/AtcManifest) and domain entities (@/domain/entities).
 * Keeps domain entities pure from external API shapes (Clean Architecture).
 *
 * Follows:
 * - adapt-mapper-translation: Use mappers to translate between layers
 * - dep-data-crossing-boundaries: Use simple data structures across boundaries
 */

import type { Station as DomainStation, Scenario as DomainScenario } from '@/domain/entities';
import { createStation, createScenario, withScenarios } from '@/domain/entities';
import type { AtcStation, AtcScenario } from '@/types/AtcManifest';

/**
 * Maps between ATC manifest types and domain entities.
 * Pure functions with no side effects.
 */
export const StationMapper = {
  /**
   * Map legacy AtcScenario to domain Scenario entity.
   * Handles field name transformations (snake_case -> camelCase).
   */
  toDomainScenario(legacy: AtcScenario): DomainScenario {
    return createScenario({
      id: legacy.scenario_id,
      cdnUrl: legacy.cdn_url,
      sizeBytes: legacy.size_bytes,
      uploadedAt: new Date(legacy.uploaded_at),
    });
  },

  /**
   * Map domain Scenario to legacy format.
   * Handles field name transformations (camelCase -> snake_case).
   */
  toLegacyScenario(scenario: DomainScenario): AtcScenario {
    return {
      scenario_id: scenario.id,
      cdn_url: scenario.cdnUrl,
      size_bytes: scenario.sizeBytes,
      uploaded_at: scenario.uploadedAt.toISOString(),
    };
  },

  /**
   * Map legacy AtcStation to domain Station entity.
   * Generates compound ID from airportCode and stationType.
   */
  toDomainStation(legacy: AtcStation): DomainStation {
    const scenarios = legacy.scenarios
      ? legacy.scenarios.map((s) => StationMapper.toDomainScenario(s))
      : undefined;

    return createStation({
      id: `${legacy.airportCode}-${legacy.stationType}`,
      airportCode: legacy.airportCode,
      stationType: legacy.stationType,
      displayName: legacy.displayName,
      airportName: legacy.airportName,
      iataCode: legacy.iataCode,
      icaoCode: legacy.icaoCode,
      localCode: legacy.localCode,
      city: legacy.city,
      country: legacy.country,
      timeZone: legacy.timeZone,
      radioEffectPreset: legacy.radioEffectPreset,
      scenarioCount: legacy.scenarioCount,
      totalSizeBytes: legacy.totalSizeBytes,
      manifestUrl: legacy.manifestUrl,
      updatedAt: new Date(legacy.updatedAt),
      scenarios,
    });
  },

  /**
   * Map domain Station to legacy format.
   * Converts Date to ISO string and maps scenarios.
   */
  toLegacyStation(station: DomainStation): AtcStation {
    return {
      airportCode: station.airportCode,
      stationType: station.stationType,
      displayName: station.displayName,
      airportName: station.airportName,
      iataCode: station.iataCode,
      icaoCode: station.icaoCode,
      localCode: station.localCode,
      city: station.city,
      country: station.country,
      timeZone: station.timeZone,
      radioEffectPreset: station.radioEffectPreset,
      scenarioCount: station.scenarioCount,
      totalSizeBytes: station.totalSizeBytes,
      manifestUrl: station.manifestUrl,
      updatedAt: station.updatedAt.toISOString(),
      scenarios: station.scenarios
        ? [...station.scenarios].map((s) => StationMapper.toLegacyScenario(s))
        : null,
    };
  },

  /**
   * Create a domain Station with scenarios from an existing station and scenarios array.
   * Uses the withScenarios entity function for immutable update.
   */
  withDomainScenarios(
    station: DomainStation,
    legacyScenarios: AtcScenario[]
  ): DomainStation {
    const domainScenarios = legacyScenarios.map((s) =>
      StationMapper.toDomainScenario(s)
    );
    return withScenarios(station, domainScenarios);
  },
};
